import React from 'react';
import FormFieldWrapper from './FormFieldWrapper';
import { FormFieldConfig } from '../../types/mold';

interface EditableModalProps<T> {
  isOpen: boolean;
  isEditMode: boolean;
  header?: string;
  item: Partial<T> | null; // Allow partial items to handle optional fields
  fields: FormFieldConfig<T>[]; // Use FormFieldConfig type for fields
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onToggleChange?: (name: keyof T, value: boolean) => void; // For boolean fields
  validationErrors?: Partial<Record<keyof T, string>>; // Ensure validation errors match the item keys
}

const EditableModal = <T extends Record<string, any>>({
  isOpen,
  isEditMode,
  header,
  item,
  fields,
  onClose,
  onSubmit,
  onInputChange,
  onToggleChange,
  validationErrors = {},
}: EditableModalProps<T>) => {
  if (!isOpen || !item) return null;
  const headerText = header || (isEditMode ? 'แก้ไขข้อมูล' : 'เพิ่มข้อมูลใหม่');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-xl mb-4 font-semibold">
          {headerText}
        </h2>
        <form onSubmit={onSubmit}>
          {fields.map((field) => (
            <div className="mb-4" key={String(field.key)}>
              <FormFieldWrapper
                config={field}
                value={item[field.key as keyof T] ?? ''} // Ensure a fallback value for undefined fields
                onChange={(value) => {
                  if (field.type === 'boolean' && onToggleChange) {
                    onToggleChange(field.key as keyof T, value);
                  } else {
                    onInputChange({
                      target: { name: field.key as string, value },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }
                }}
              />
              {validationErrors[field.key as keyof T] && (
                <p className="mt-1 text-sm text-red-600">
                  {validationErrors[field.key as keyof T]}
                </p>
              )}
            </div>
          ))}
          <div className="flex justify-end gap-1">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-full"
            >
              ยกเลิก
            </button>
            <button type="submit" className="flex flex-row items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 rounded-full">
              {isEditMode ? 'บันทึก' : 'สร้างใหม่'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditableModal;
